import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  Input,
  Optional,
  Self,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormGroupDirective, NgControl, NgForm } from '@angular/forms';
import {
  CanDisable,
  CanDisableRipple,
  CanUpdateErrorState,
  ErrorStateMatcher,
  HasTabIndex,
  mixinDisabled,
  mixinDisableRipple,
  mixinErrorState,
  mixinTabIndex,
} from '@angular/material/core';
import { MatFormField, MatFormFieldControl, MAT_FORM_FIELD } from '@angular/material/form-field';
import { MatLegacySelect } from '@angular/material/legacy-select';
import { MatSelect } from '@angular/material/select';
import { Subject } from 'rxjs';

import {
  IControlValueAccessor,
  IHasChangeDetectorRef,
  mixinControlValueAccessor,
} from '../../mixins/control-value-accesor.mixin';

type BooleanDefault = boolean | null;

export class BooleanDefaultSelectBase implements IHasChangeDetectorRef {
  constructor(
    public _changeDetectorRef: ChangeDetectorRef,
    public _elementRef: ElementRef,
    public ngControl: NgControl,
    public _parentFormGroup: FormGroupDirective,
    public _parentForm: NgForm,
    public _defaultErrorStateMatcher: ErrorStateMatcher,
    public stateChanges: Subject<void>
  ) {}
}

/* tslint:disable-next-line */
export const _BooleanDefaultSelectMixinBase = mixinControlValueAccessor(
  mixinDisableRipple(mixinTabIndex(mixinDisabled(mixinErrorState(BooleanDefaultSelectBase)))),
  null
);

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: BooleanDefaultSelectBaseComponent,
      multi: true,
    },
  ],
  selector: 'csl-boolean-default-select',
  /* tslint:disable-next-line */
  inputs: ['disabled', 'disableRipple', 'tabIndex'],
  templateUrl: './boolean-default-select.component.html',
  styleUrls: ['./boolean-default-select.component.scss'],
  host: {
    class: 'csl-boolean-default-select',
    '[attr.id]': 'id',
    '[attr.aria-invalid]': 'errorState',
    'attr.aria-describedby': 'describedBy',
    'attr.aria-labelledby': '_parentFormField?.getLabelId()',
    '[class.csl-boolean-default-select-disabled]': 'disabled',
    '[class.csl-boolean-default-select-invalid]': 'errorState',
    '[class.csl-boolean-default-select-empty]': 'empty',
    '(focus)': '_onFocus()',
    '(blur)': '_onBlur()',
  },
})
export class BooleanDefaultSelectBaseComponent
  extends _BooleanDefaultSelectMixinBase
  implements
    IControlValueAccessor,
    MatFormFieldControl<BooleanDefault>,
    CanDisable,
    CanUpdateErrorState,
    HasTabIndex,
    CanDisableRipple {
  // private _focused: boolean = false;
  private _placeholder = '';
  private _required = false;

  static nextId = 0;

  stateChanges: Subject<void> = new Subject();

  describedBy = '';

  controlType = 'csl-boolean-default-select';

  id = `csl-boolean-default-select-${BooleanDefaultSelectBaseComponent.nextId++}`;

  @ViewChild(MatLegacySelect, { static: true }) control: MatSelect;

  @Input()
  get disabled(): boolean {
    return super.disabled;
  }
  override set disabled(value: boolean) {
    super.disabled = value;
    this.stateChanges.next();
  }

  @Input()
  get placeholder(): string {
    return this._placeholder;
  }
  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  get focused(): boolean {
    return this.control.focused;
  }

  get empty(): boolean {
    return this.control.empty;
  }

  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  constructor(
    _changeDetectorRef: ChangeDetectorRef,
    _defaultErrorStateMatcher: ErrorStateMatcher,
    elementRef: ElementRef,
    @Optional() @Self() public ngControl: NgControl,
    @Optional() _parentForm: NgForm,
    @Optional() _parentFormGroup: FormGroupDirective,
    @Optional() stateChanges: Subject<void>,
    @Optional() @Inject(MAT_FORM_FIELD) protected _parentFormField: MatFormField
  ) {
    super(
      _changeDetectorRef,
      elementRef,
      ngControl,
      _parentFormGroup,
      _parentForm,
      _defaultErrorStateMatcher,
      stateChanges
    );

    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngDoCheck() {
    if (this.ngControl) {
      this.updateErrorState();
    }
  }

  onContainerClick(event: MouseEvent): void {
    if (!this.disabled) {
      this.control.onContainerClick();
    }
  }

  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }

  _onFocus() {
    this.control._onFocus();

    if (!this.disabled) {
      this.stateChanges.next();
    }
  }

  _onBlur() {
    this.control._onBlur();

    if (!this.disabled) {
      this.onTouched();
      this.stateChanges.next();
    }
  }
}
